import { loginSchema, type LoginInput } from '@esportsbudget/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, PlayCircle } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { errorMessage, fieldErrors } from '@/lib/errorMessage';
import { useDemoSession } from '@/lib/useDemoSession';
import { useAuth } from '@/providers/AuthProvider';

export function LoginPage() {
  const { t } = useTranslation(['auth', 'common', 'errors', 'validation']);
  const { login, config } = useAuth();
  const navigate = useNavigate();

  const [formError, setFormError] = useState<string | null>(null);

  const demoMutation = useDemoSession(setFormError);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values);
      navigate('/', { replace: true });
    } catch (error) {
      const fields = fieldErrors(error, t);
      for (const [field, message] of Object.entries(fields)) {
        setError(field as keyof LoginInput, { message });
      }
      if (Object.keys(fields).length === 0) setFormError(errorMessage(error, t));
    }
  });

  const canRegister = config?.registrationMode !== 'CLOSED';

  return (
    <AuthShell
      title={t('auth:loginTitle')}
      subtitle={t('auth:loginSubtitle')}
      footer={
        canRegister ? (
          <>
            {t('auth:noAccount')}{' '}
            <Link to="/register" className="font-medium text-accent hover:underline">
              {t('auth:signUp')}
            </Link>
          </>
        ) : (
          t('auth:registrationClosed')
        )
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <Input
          label={t('auth:username')}
          placeholder={t('auth:usernamePlaceholder')}
          autoComplete="username"
          autoFocus
          error={errors.username ? t(errors.username.message ?? '', { defaultValue: errors.username.message ?? '' }) : null}
          {...register('username')}
        />

        <Input
          type="password"
          label={t('auth:password')}
          placeholder={t('auth:passwordPlaceholder')}
          autoComplete="current-password"
          error={errors.password ? t(errors.password.message ?? '', { defaultValue: errors.password.message ?? '' }) : null}
          {...register('password')}
        />

        {formError && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-[10px] bg-danger-soft px-3 py-2.5 text-[13px] text-danger"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{formError}</span>
          </div>
        )}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          {t('auth:signIn')}
        </Button>

        <div className="flex justify-center">
          <Link to="/recover" className="text-[13px] text-muted hover:text-text hover:underline">
            {t('auth:forgotPassword')}
          </Link>
        </div>
      </form>

      {/* Демо (F-25). Понять по скриншотам, подходит ли тебе учёт финансов,
          нельзя — нужно потрогать. Аккаунт исчезает вместе с данными. */}
      {config?.demoEnabled && (
        <div className="mt-5 border-t border-border pt-5">
          <Button
            variant="secondary"
            fullWidth
            icon={<PlayCircle />}
            loading={demoMutation.isPending}
            onClick={() => demoMutation.mutate()}
          >
            {t('auth:tryDemo')}
          </Button>
          <p className="mt-2 text-center text-[12px] text-subtle">{t('auth:tryDemoHint')}</p>
        </div>
      )}

      {/* Общеизвестные пароли показываем только на открытом демо-стенде.
          На закрытом сервере это была бы подсказка для посторонних. */}
      {config?.registrationMode === 'OPEN' && (
      <div className="mt-6 rounded-[10px] border border-border bg-surface-2 px-3.5 py-3 text-[12.5px] text-muted">
        <p className="mb-1">{t('auth:demoHint')}</p>
        <p>
          {t('nav:admin', { ns: 'nav', defaultValue: 'Админ-панель' })}:{' '}
          <span className="font-medium text-text">admin / admin123</span> → <code>/admin</code>
        </p>
      </div>
      )}
    </AuthShell>
  );
}
